import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import * as Mui from "@mui/material";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { useAppThunkDispatch } from "redux/store";
import { Office } from "model/office";
import { updateOffice } from "redux/slice/offices";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { LabeledField } from "component/shared/profile-refresh/labeled-field";
import { stripHtml } from "component/shared/profile/tab-bio/validation";
import { styles } from "./styles";
import { TabHandle } from "component/shared/profile-refresh/tab-handle";

interface Props extends WithStyles<typeof styles> {
	office: Office;
	onDirtyChange: () => void;
}

const IMAGE_DESCRIPTION =
	"Add an image of your office to display on the office's detail page on your website.";
const HEADER_DESCRIPTION =
	"Add a header to display above your office description on the office's detail page. Consider using your office name, tagline, or a welcoming message.";
const DESCRIPTION_DESCRIPTION =
	"Add a description for your office that will appear on your website's office detail page. Share information about your office, the services you offer, and the areas you serve.";

const DESCRIPTION_MAX = 5000;

type ImageSource = "imageUrl" | "uploadImage";

export const WebsiteTab = forwardRef<TabHandle, Props>(({ office, classes = {}, onDirtyChange }, ref) => {
	const dispatch = useAppThunkDispatch();

	// Which radio is selected. An office has one photoUrl, but the two modes need to hold their
	// values independently: switching to Upload must not show the typed URL as an "uploaded"
	// image, and switching back must not lose what was typed.
	const [imageSource, setImageSource] = useState<ImageSource>(office.photoUrl ? "imageUrl" : "uploadImage");
	const [imageUrl, setImageUrl] = useState(office.photoUrl || "");
	const [uploadedUrl, setUploadedUrl] = useState("");
	const [header, setHeader] = useState(office.header || "");
	const [description, setDescription] = useState(office.description || "");
	const [uploadError, setUploadError] = useState(false);

	// The value that actually gets saved is whichever mode is selected.
	const photoUrl = imageSource === "imageUrl" ? imageUrl : uploadedUrl;

	const isDirty = () =>
		photoUrl !== (office.photoUrl || "") ||
		header !== (office.header || "") ||
		description !== (office.description || "");

	useEffect(() => { onDirtyChange(); }, [onDirtyChange, photoUrl, header, description]);

	const descriptionLength = stripHtml(description).trim().length;
	const descriptionError = descriptionLength > DESCRIPTION_MAX;

	useImperativeHandle(ref, () => ({
		isDirty,
		cancel: () => {
			setImageUrl(office.photoUrl || "");
			setUploadedUrl("");
			setHeader(office.header || "");
			setDescription(office.description || "");
			setImageSource(office.photoUrl ? "imageUrl" : "uploadImage");
			setUploadError(false);
		},
		save: async () => {
			if (descriptionError) {
				return;
			}
			await dispatch(updateOffice({
				office: { ...office, photoUrl, header, description },
			}));
		},
	}));

	return (
		<div className={classes.section}>
			<Mui.Typography className={classes.sectionTitle}>Office Page</Mui.Typography>

			<Mui.Typography className={classes.subTitle}>Image</Mui.Typography>
			<Mui.Typography className={classes.sectionDescription}>{IMAGE_DESCRIPTION}</Mui.Typography>
			<div className={classes.formColumn}>
				<Mui.RadioGroup
					className={classes.radioGroup}
					value={imageSource}
					onChange={(event) => setImageSource(event.target.value as ImageSource)}
				>
					<Mui.FormControlLabel value="imageUrl" control={<Mui.Radio color="secondary" />} label="Image URL" />
					{imageSource === "imageUrl" && (
						<div className={classes.radioDisclosure}>
							<LabeledField label="URL" value={imageUrl} onChange={setImageUrl} />
						</div>
					)}
					<Mui.FormControlLabel value="uploadImage" control={<Mui.Radio color="secondary" />} label="Upload Image" />
				</Mui.RadioGroup>

				{imageSource === "uploadImage" && (
					<div className={classes.radioDisclosure}>
						{uploadedUrl ? (
							<div className={classes.imagePreview}>
								<img className={classes.imagePreviewImg} src={uploadedUrl} alt="Office" />
								<Mui.IconButton
									className={classes.imageClearButton}
									aria-label="Remove image"
									size="small"
									onClick={() => setUploadedUrl("")}
								>
									<Icons.X size={14} />
								</Mui.IconButton>
							</div>
						) : (
							<>
								<PhotoUploadDialogButton
									multiple={false}
									maxFiles={1}
									resourceType="image"
									variant="text"
									label="Upload Image"
									icon={<Icons.Upload size={14} />}
									callback={(uploads) => {
										const upload = uploads.find((u) => u.url);
										if (upload && upload.url) {
											setUploadedUrl(upload.url);
											setUploadError(false);
										} else {
											setUploadError(true);
										}
									}}
								/>
								{uploadError && (
									<Mui.Typography className={classes.errorText}>Upload Error</Mui.Typography>
								)}
							</>
						)}
					</div>
				)}
			</div>

			<Mui.Typography className={classes.subTitle}>Header</Mui.Typography>
			<Mui.Typography className={classes.sectionDescription}>{HEADER_DESCRIPTION}</Mui.Typography>
			<div className={classes.editorBlock}>
				<RichTextEditor toolbarId="officeHeaderRefresh" value={header} onChange={setHeader} />
			</div>

			<Mui.Typography className={classes.subTitle}>Description</Mui.Typography>
			<Mui.Typography className={classes.sectionDescription}>{DESCRIPTION_DESCRIPTION}</Mui.Typography>
			<div className={classes.editorBlock}>
				<RichTextEditor toolbarId="officeDescriptionRefresh" value={description} onChange={setDescription} />
				{/* The Figma drops this counter, but the 5000-char cap is still enforced on save —
				    a silent truncation is worse than a visible count, so it stays. */}
				<Mui.Typography className={classes.characterCount}>
					{descriptionLength}/{DESCRIPTION_MAX} characters
				</Mui.Typography>
				{descriptionError && (
					<Mui.Typography className={classes.errorText}>Max {DESCRIPTION_MAX} characters.</Mui.Typography>
				)}
			</div>
		</div>
	);
});
